import {
  type ContentAPIFactory,
  type ContentWindowApi,
  type ContentAPI,
} from '@canvas/frame-api-types';

import { CanvasContentAPI, isFrameAPI } from '../canvas-frame-api';

/**
 * Other content window APIs should inherit this class
 * This is purely just for the convenience of implementing shared logic across apis
 */
abstract class BaseContentAPI implements ContentWindowApi {
  constructor(
    private readonly contentAPIFactory: ContentAPIFactory = (rawContentAPI) =>
      isFrameAPI(rawContentAPI) ? new CanvasContentAPI(rawContentAPI) : undefined
  ) {}

  abstract title: string;
  abstract href: string;
  abstract isTopLevel: boolean;

  get contentAPI(): ContentAPI | undefined {
    // eslint-disable-next-line no-restricted-syntax
    const rawContentAPI = this.parent?.contentAPI;

    return this.contentAPIFactory(rawContentAPI);
  }
  protected abstract parent: Window | undefined;

  reload() {
    this.parent?.location.reload();
  }
}

/**
 * Exposes `window` methods for a host frame initialised as a smart link embed.
 *
 * We don't have access to the `parent` when we are in a smart link, so
 * accessing `window.parent` across domains would throw an error.
 */
export class SmartLinkAPI extends BaseContentAPI {
  get title() {
    return globalThis.document.title;
  }

  set title(value: string) {
    globalThis.document.title = value;
  }

  get href() {
    return globalThis.location.href;
  }

  get isTopLevel() {
    return false;
  }

  get parent() {
    return window;
  }
}

/**
 * Exposes `window` methods for a host frame initialised in normal EAP mode.
 */
export class NativeAPI extends BaseContentAPI implements ContentWindowApi {
  get title() {
    // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent.document.title;
  }

  set title(value: string) {
    // eslint-disable-next-line no-restricted-syntax
    globalThis.parent.document.title = value;
  }

  get href() {
    // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent.location.href;
  }

  get isTopLevel() {
    // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent === window;
  }

  get parent() {
    // eslint-disable-next-line no-restricted-syntax
    return globalThis.parent;
  }
}

export class DefaultFrameApi extends NativeAPI {}
